import { CSSProperties, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useLayout } from "@/contexts/LayoutContext";
import { useSignalRNotifications } from "@/hooks/useSignalRNotifications";
import {
  getNotifications, dismissTask, removeNotification,
} from "@/api/notifications/api";
import { NotificationItem, TaskReminderItem } from "@/types/domain";
import {
  readCache, writeCache, isDismissed, markDismissed, cleanupDismissed,
} from "./notificationsStorage";
import {
  timeAgo, taskPriorityColor, notificationTypeColor, notificationTypeLabel,
  taskPriorityLabel,
} from "./notificationHelpers";

export const TASKS_TOAST_ID = "tasks-reminder";

export type UseNotificationsResult = {
  count: number;
  notifications: NotificationItem[];
  tasks: TaskReminderItem[];
  handleDismissTask: (task: TaskReminderItem) => void;
  dismissNotification: (id: string, sourceType?: number) => void;
};

const notificationGroupToastId = (sourceType?: number) => `notif-type-${sourceType ?? "unknown"}`;

const notificationLink = (sourceType?: number, sourceId?: string): string | null => {
  if (!sourceId) return null;
  switch (sourceType) {
    case 1:  return `/AlertResultsMaintenance.aspx?AlertID=${sourceId}`; // Alert
    case 2:  return `/EditTask.aspx?taskid=${sourceId}`;                  // Task
    case 6:  return `/AccountSettings`;                                   // External
    case 8:  return `/Project/Details/${sourceId}`;                       // Workflow
    case 10: return `/Document/DownloadDocument/${sourceId}`;             // Export
    default: return null;                                                 // User — no link
  }
};

const showNotificationsToast = (group: NotificationItem[], databaseId: string) => {
  const sourceType = group[0].Source?.SourceType;
  const color = notificationTypeColor(sourceType);
  const label = notificationTypeLabel(sourceType);
  const id = notificationGroupToastId(sourceType);
  if (group.length === 1) {
    const n = group[0];
    const link = notificationLink(sourceType, n.Source?.SourceID);
    toast(n.Subject, {
      id,
      description: (
        <span style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "flex-end" }}>
          <span>{n.Body || (n.Owner?.Name ? `From: ${n.Owner.Name}` : label)}</span>
          {n.SendDate && <span style={{ fontSize: "11px", color: "#9ca3af", flexShrink: 0 }}>{timeAgo(n.SendDate)}</span>}
        </span>
      ),
      duration: Infinity,
      closeButton: true,
      onDismiss: () => markDismissed(databaseId, n.Id),
      ...(link ? { action: { label: "View", onClick: () => { markDismissed(databaseId, n.Id); toast.dismiss(id); window.location.assign(link); } } } : {}),
      classNames: { toast: "notification-toast" },
      style: { "--notification-accent": color.accent } as CSSProperties,
    });
  } else {
    toast(`You have ${group.length} unread ${label} notifications`, {
      id,
      description: "",
      duration: Infinity,
      closeButton: true,
      onDismiss: () => group.forEach((n) => markDismissed(databaseId, n.Id)),
      action: { label: "View all", onClick: () => window.location.assign("/NotificationsList.aspx") },
      classNames: { toast: "notification-toast" },
      style: { "--notification-accent": color.accent } as CSSProperties,
    });
  }
};

const showTasksToast = (ts: TaskReminderItem[], viewTasksRoute: string) => {
  if (ts.length === 0) return;
  if (ts.length === 1) {
    const t = ts[0];
    const color = taskPriorityColor(t.Priority);
    const label = taskPriorityLabel(t.Priority);
    const taskUrl = `${window.location.origin}${viewTasksRoute}?taskid=${t.ID}${
      t.RecMaster ? `&recurrenceMaster=${t.RecMaster}${t.RecurrenceIndex != null ? `&recurrenceIndex=${t.RecurrenceIndex}` : ""}` : ""
    }`;
    toast(t.Subject ?? "Task reminder", {
      id: TASKS_TOAST_ID,
      description: t.Company?.CompanyName ? `${label} · ${t.Company.CompanyName}` : label,
      duration: Infinity,
      closeButton: true,
      action: { label: "View", onClick: () => window.location.assign(taskUrl) },
      classNames: { toast: "notification-toast" },
      style: { "--notification-accent": color.accent } as CSSProperties,
    });
  } else {
    toast(`You have ${ts.length} task reminders`, {
      id: TASKS_TOAST_ID,
      duration: Infinity,
      closeButton: true,
      action: { label: "View all", onClick: () => window.location.assign(`${window.location.origin}${viewTasksRoute}`) },
      classNames: { toast: "notification-toast" },
      style: { "--notification-accent": "#f59e0b" } as CSSProperties,
    });
  }
};

export const useNotifications = (): UseNotificationsResult => {
  const { data } = useLayout();
  const routes = data.appRoutes;
  const databaseId = data.databaseId;

  const [count, setCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [tasks, setTasks] = useState<TaskReminderItem[]>([]);
  const initialLoadRef = useRef(true);

  const applyResult = (notifs: NotificationItem[], ts: TaskReminderItem[], showToasts: boolean) => {
    cleanupDismissed(databaseId, notifs.map((n) => n.Id));

    setCount(notifs.length + ts.length);
    setNotifications(notifs);
    setTasks(ts);

    if (!showToasts) return;

    // Group undismissed notifications by sourceType (one toast per type, matching legacy)
    const undismissed = notifs.filter((n) => !isDismissed(databaseId, n.Id));
    const grouped = undismissed.reduce<Record<string, NotificationItem[]>>((acc, n) => {
      const key = String(n.Source?.SourceType ?? "unknown");
      (acc[key] ??= []).push(n);
      return acc;
    }, {});
    Object.values(grouped).forEach((group) => showNotificationsToast(group, databaseId));

    // All tasks combined into one toast (matching legacy)
    showTasksToast(ts, routes.viewTasks);
  };

  const fetchNotifications = (showToasts = false) => {
    // On first call, check localStorage cache first (legacy behaviour: show immediately, then refresh)
    if (showToasts) {
      try {
        const result = readCache(databaseId);
        if (result) {
          applyResult(result.notifications, result.tasks, true);
          // Toasts already shown from cache — don't show again when server response arrives
          initialLoadRef.current = false;
        }
      } catch { /* non-critical */ }
    }

    getNotifications()
      .then((result) => {
        if (!result) return;
        const notifs = result.notifications ?? [];
        const ts = result.tasks ?? [];
        writeCache(databaseId, { notifications: notifs, tasks: ts });
        // Only show toasts on initial page load fetch, not on polling refreshes
        applyResult(notifs, ts, showToasts && initialLoadRef.current);
        initialLoadRef.current = false;
      })
      .catch(() => {});
  };

  const handleDismissTask = (task: TaskReminderItem) => {
    // Dismiss the tasks toast immediately — if other tasks remain it will re-appear on next poll
    toast.dismiss(TASKS_TOAST_ID);
    dismissTask(task.ID, task.RecMaster ?? null, task.RecurrenceIndex ?? null)
      .then(() => {
        setTasks((prev) => {
          const updated = prev.filter((t) => t.ID !== task.ID);
          // Remove from shared cache so legacy notify.js and next page load don't restore it
          writeCache(databaseId, { notifications, tasks: updated });
          return updated;
        });
        setCount((c) => Math.max(0, c - 1));
      })
      .catch(() => { toast.error("Failed to dismiss task."); });
  };

  const dismissNotification = (id: string, sourceType?: number) => {
    markDismissed(databaseId, id);
    // Dismiss the toast for this notification type — if others in the group remain they'll reappear on next poll
    toast.dismiss(notificationGroupToastId(sourceType));
    removeNotification(id)
      .then(() => {
        setNotifications((prev) => prev.filter((n) => n.Id !== id));
        setCount((c) => Math.max(0, c - 1));
      })
      .catch(() => { toast.error("Failed to dismiss notification."); });
  };

  useEffect(() => {
    // Delay so page-critical API requests get the connection first; cache is shown immediately by fetchNotifications
    const t = setTimeout(() => fetchNotifications(true), 500);
    return () => clearTimeout(t);
  }, []);

  // SignalR pushes the same PascalCase shape — no adaptation needed, merge directly into state
  const handleNotification = (n: NotificationItem) => {
    setNotifications((prev) => {
      const updated = [...prev, n];
      const group = updated.filter(
        (x) => x.Source?.SourceType === n.Source?.SourceType && !isDismissed(databaseId, x.Id)
      );
      showNotificationsToast(group, databaseId);
      return updated;
    });
    setCount((c) => c + 1);
  };

  const handleTask = (t: TaskReminderItem) => {
    setTasks((prev) => {
      const updated = [...prev, t];
      showTasksToast(updated, routes.viewTasks);
      return updated;
    });
    setCount((c) => c + 1);
  };

  useSignalRNotifications({
    onNotification: handleNotification,
    onTask: handleTask,
  });

  return { count, notifications, tasks, handleDismissTask, dismissNotification };
};
